/**
 * Intake against target — the spec's "Intake vs target · 30 days".
 *
 * Bars for daily intake, a dashed rule at the target, a solid one at the
 * average. Hand-drawn SVG rather than a charting library: it's a bar series and
 * two horizontal lines, and a library would arrive with its own colour defaults
 * to fight the rationed palette.
 *
 * The decision that shapes it: **unlogged days are gaps, not zeros.** The API
 * omits them (see `services/progress.daily_intake`) and this preserves that —
 * a day with no bar reads as "didn't log", where a zero-height bar would read
 * as "ate nothing". Those are very different claims and only one is true.
 */

import type { IntakeSeries } from "@calorio/core";

interface IntakeChartProps {
  intake: IntakeSeries;
  height?: number;
}

export function IntakeChart({ intake, height = 150 }: IntakeChartProps) {
  const { points, average_kcal, target_kcal, window_days } = intake;

  if (points.length < 2) {
    return <p className="muted">A few more days of logging and the trend shows up here.</p>;
  }

  const width = 640;
  const padY = 12;

  // Scale to the target as well as the data: a chart whose ceiling is the
  // highest bar makes every week look identical, whether you were 200 over or
  // 200 under. Anchoring on the target is what makes the comparison legible.
  const maxKcal = Math.max(...points.map((p) => p.kcal), target_kcal ?? 0) * 1.1;

  // Lay days out by *date*, not by index, so a gap in logging is visibly a gap.
  const first = Date.parse(points[0]!.local_date);
  const last = Date.parse(points.at(-1)!.local_date);
  const span = last - first || 1;

  const x = (iso: string) => ((Date.parse(iso) - first) / span) * (width - 20) + 10;
  const y = (kcal: number) => padY + (1 - kcal / maxKcal) * (height - padY * 2);
  // One bar per day of the window, so bars don't fatten when logging is sparse.
  const barWidth = Math.max(3, (width - 20) / window_days - 2);

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`Daily intake over ${window_days} days${
          target_kcal ? `, against a ${target_kcal} kcal target` : ""
        }`}
      >
        {points.map((point) => {
          const top = y(point.kcal);
          const over = target_kcal !== null && point.kcal > target_kcal;
          return (
            <rect
              key={point.local_date}
              x={x(point.local_date) - barWidth / 2}
              y={top}
              width={barWidth}
              height={Math.max(height - padY - top, 1)}
              rx={1.5}
              // Over-target is the one thing worth colouring differently; the
              // rest stay in the neutral chart hue.
              fill={over ? "var(--alert)" : "var(--carbs)"}
              opacity={over ? 0.85 : 0.55}
            />
          );
        })}

        {target_kcal !== null && (
          <line
            x1={0}
            x2={width}
            y1={y(target_kcal)}
            y2={y(target_kcal)}
            stroke="var(--ink-62)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        )}

        {average_kcal !== null && (
          <line
            x1={0}
            x2={width}
            y1={y(average_kcal)}
            y2={y(average_kcal)}
            stroke="var(--weekly-avg)"
            strokeWidth={2}
          />
        )}
      </svg>

      <figcaption
        style={{
          display: "flex",
          gap: "var(--sp-4)",
          fontSize: "var(--step--1)",
          color: "var(--ink-62)",
        }}
      >
        {average_kcal !== null && (
          <span>
            <Swatch color="var(--weekly-avg)" /> avg{" "}
            <span className="num">{Math.round(average_kcal).toLocaleString()}</span>
          </span>
        )}
        {target_kcal !== null && (
          <span>
            <Swatch color="var(--ink-62)" dashed /> target{" "}
            <span className="num">{target_kcal.toLocaleString()}</span>
          </span>
        )}
        <span style={{ marginLeft: "auto" }}>
          <span className="num">{points.length}</span> of{" "}
          <span className="num">{window_days}</span> days logged
        </span>
      </figcaption>
    </figure>
  );
}

function Swatch({ color, dashed = false }: { color: string; dashed?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 12,
        height: 0,
        verticalAlign: "middle",
        marginRight: 4,
        borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}`,
      }}
    />
  );
}
